import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { TranslationKey } from './i18n';

/**
 * Calls › AI summary (the operator's CR, 2026-10-29): an automatic transcript and summary of every
 * call made while AI summaries are on — never one made before. Read by whoever was connected to the
 * call (all of it, a late joiner too) and administrators; a new version, the settings and the usage
 * are the administrators'. The rules are the API's (apps/backend/src/domain/call-ai.ts).
 */

export type AiState =
  | 'HIDDEN'
  | 'NOT_AVAILABLE'
  | 'WAITING_FOR_RECORDING'
  | 'QUEUED'
  | 'TRANSCRIBING'
  | 'SUMMARIZING'
  | 'RETRY_PENDING'
  | 'FAILED'
  | 'READY'
  | 'NO_SPEECH';
export type AiPending = 'QUEUED' | 'TRANSCRIBING' | 'SUMMARIZING' | 'RETRY_PENDING';
export type AiNotAvailableReason =
  | 'NOT_ELIGIBLE'
  | 'NO_RECORDING'
  | 'RECORDING_PARTIAL'
  | 'RECORDING_FAILED'
  | 'RECORDING_EXPIRED'
  | 'NOT_QUEUED'
  | 'CANCELLED';

export interface ActionItem {
  description: string;
  owner: string | null;
  dueDate: string | null;
}

export interface AiSummaryContent {
  revision: number;
  outcome: 'SUMMARY' | 'NO_SPEECH';
  createdAt: string;
  regenerated: boolean;
  language: 'ar' | 'en';
  title?: string;
  overview?: string;
  keyPoints?: string[];
  decisions?: string[];
  actionItems?: ActionItem[];
  openQuestions?: string[];
}

export interface AiSummaryView {
  callId: string;
  state: AiState;
  reason: AiNotAvailableReason | null;
  /** A new version being made while the current one is shown. */
  pending: AiPending | null;
  summary: AiSummaryContent | null;
  transcript: { state: 'NONE' | 'READY' | 'EXPIRED'; languages: string[]; purgedAt: string | null };
  /** The administrators' alone. */
  admin: {
    job: {
      id: string;
      kind: 'AUTOMATIC' | 'REGENERATE';
      status: string;
      stage: string;
      attempts: number;
      maxAttempts: number;
      nextAttemptAt: string | null;
      errorCode: string | null;
      errorAt: string | null;
      createdAt: string;
      finishedAt: string | null;
    } | null;
    summaryModel: string | null;
    transcriptionModel: string | null;
    promptVersion: string | null;
    schemaVersion: string | null;
    revisions: number;
    eligible: boolean;
  } | null;
  can: { readTranscript: boolean; regenerate: boolean };
}

export interface TranscriptView {
  callId: string;
  aiGenerated: true;
  model: string;
  createdAt: string;
  languages: string[];
  audioSeconds: number;
  segments: Array<{ index: number; startSeconds: number; endSeconds: number; text: string }>;
}

export interface SummaryRevision {
  revision: number;
  isCurrent: boolean;
  outcome: 'SUMMARY' | 'NO_SPEECH';
  createdAt: string;
  supersededAt: string | null;
  model: string;
  transcriptionModel: string | null;
  promptVersion: string;
  schemaVersion: string;
  createdBy: { id: string; fullName: string; fullNameAr: string | null } | null;
  reason: string | null;
  content: Omit<AiSummaryContent, 'revision' | 'outcome' | 'createdAt' | 'regenerated'> | null;
}

export interface AiAccessRow {
  id: string;
  action: 'SUMMARY_VIEWED' | 'SUMMARY_DENIED' | 'TRANSCRIPT_VIEWED' | 'TRANSCRIPT_DENIED' | 'REGENERATE_REQUESTED' | 'REGENERATE_DENIED';
  reason: string | null;
  channel: 'WEB' | 'MOBILE' | null;
  ipAddress: string | null;
  createdAt: string;
  actor: { id: string; fullName: string; fullNameAr: string | null } | null;
}

export interface AiPricing {
  asOf: string | null;
  transcriptionPerMinuteUsd: number | null;
  summaryInputPerMillionUsd: number | null;
  summaryOutputPerMillionUsd: number | null;
}

export interface AiSettings {
  enabled: boolean;
  firstEnabledAt: string | null;
  summaryLanguage: 'ar' | 'en';
  transcriptionModel: string;
  summaryModel: string;
  retryFailedJobs: boolean;
  maxConcurrentJobs: number;
  keywords: string[];
  pricing: AiPricing;
  offered: { transcription: string[]; summary: string[]; languages: Array<'ar' | 'en'> };
  limits: { minConcurrentJobs: number; maxConcurrentJobs: number; maxKeywords: number; maxKeywordLength: number };
}

export type AiBlocker =
  | 'CALLS_AI_DISABLED'
  | 'OPENAI_KEY_MISSING'
  | 'STORAGE_NOT_SET_UP'
  | 'AUDIO_TOOLS_MISSING'
  | 'MODELS_NOT_CONFIGURED'
  | 'SETTING_OFF';

export interface AiStatus {
  effective: boolean;
  processing: boolean;
  deploymentEnabled: boolean;
  providerConfigured: boolean;
  storageConfigured: boolean;
  toolsAvailable: boolean;
  modelsConfigured: boolean;
  settingEnabled: boolean;
  blockers: AiBlocker[];
  tools: { ffmpeg: string | null; ffprobe: string | null };
  models: { transcription: string[]; summary: string[] };
  firstEnabledAt: string | null;
  queue: { queued: number; running: number; retryPending: number; failedLastDay: number };
}

export interface AiUsage {
  from: string;
  to: string;
  jobs: { total: number; succeeded: number; failed: number; cancelled: number; active: number };
  retries: number;
  transcription: { minutes: number; requests: number };
  summary: { requests: number; inputTokens: number; outputTokens: number; reasoningTokens: number };
  estimatedCostUsd: number;
  unpricedJobs: number;
  summaries: { written: number; noSpeech: number };
  pricing: AiPricing;
  truncated: boolean;
}

export interface AiSettingsInput {
  enabled: boolean;
  acknowledgeNotice?: boolean;
  summaryLanguage: 'ar' | 'en';
  transcriptionModel: string;
  summaryModel: string;
  retryFailedJobs: boolean;
  maxConcurrentJobs: number;
  keywords: string[];
  pricingAsOf: string;
  priceTranscriptionPerMinuteUsd: number | null;
  priceSummaryInputPerMillionUsd: number | null;
  priceSummaryOutputPerMillionUsd: number | null;
}

export const callAiApi = {
  summary: (callId: string, statusOnly = false) => api.get<AiSummaryView>(`/calls/${callId}/ai-summary${statusOnly ? '/status' : ''}`),
  transcript: (callId: string) => api.get<TranscriptView>(`/calls/${callId}/transcript`),
  regenerate: (callId: string, body: { reason: string; requestKey: string; retranscribe: boolean }) =>
    api.post<{ jobId: string; status: string; replayed: boolean }>(`/calls/${callId}/ai-summary/regenerate`, body),
  revisions: (callId: string) => api.get<SummaryRevision[]>(`/calls/${callId}/ai-summary/revisions`),
  access: (callId: string) => api.get<AiAccessRow[]>(`/calls/${callId}/ai-summary/access`),
  status: () => api.get<AiStatus>('/calls/ai/status'),
  settings: () => api.get<AiSettings>('/calls/ai/settings'),
  saveSettings: (values: AiSettingsInput) => api.put<AiSettings>('/calls/ai/settings', values),
  usage: (days: number) => api.get<AiUsage>('/calls/ai/usage', { days }),
};

/** The states in which the page looks again (`/ai-summary/status`) — a look that is not a read. */
export const WORKING_STATES: ReadonlySet<AiState> = new Set(['WAITING_FOR_RECORDING', 'QUEUED', 'TRANSCRIBING', 'SUMMARIZING', 'RETRY_PENDING']);

export function isWorking(view: Pick<AiSummaryView, 'state' | 'pending'> | undefined): boolean {
  return !!view && (WORKING_STATES.has(view.state) || view.pending !== null);
}

/**
 * The section's state, read once in full; while a job runs, a status look every five seconds — and the
 * full read again only when the state or the version being made changes.
 */
export function useCallAiSummary(callId: string, enabled: boolean) {
  const full = useQuery({ queryKey: ['calls', 'ai', callId], queryFn: () => callAiApi.summary(callId), enabled });
  const look = useQuery({
    queryKey: ['calls', 'ai', callId, 'status'],
    queryFn: () => callAiApi.summary(callId, true),
    enabled: enabled && isWorking(full.data),
    refetchInterval: 5_000,
  });
  const changed = !!look.data && !!full.data && (look.data.state !== full.data.state || look.data.pending !== full.data.pending);
  return { full, changed };
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

export const STATE_LABEL: Record<AiState, TranslationKey> = {
  HIDDEN: 'callAi.state.NOT_AVAILABLE',
  NOT_AVAILABLE: 'callAi.state.NOT_AVAILABLE',
  WAITING_FOR_RECORDING: 'callAi.state.WAITING_FOR_RECORDING',
  QUEUED: 'callAi.state.QUEUED',
  TRANSCRIBING: 'callAi.state.TRANSCRIBING',
  SUMMARIZING: 'callAi.state.SUMMARIZING',
  RETRY_PENDING: 'callAi.state.RETRY_PENDING',
  FAILED: 'callAi.state.FAILED',
  READY: 'callAi.state.READY',
  NO_SPEECH: 'callAi.state.NO_SPEECH',
};

export const STATE_TONE: Record<AiState, 'success' | 'error' | 'warning' | 'neutral' | 'info'> = {
  HIDDEN: 'neutral',
  NOT_AVAILABLE: 'neutral',
  WAITING_FOR_RECORDING: 'info',
  QUEUED: 'info',
  TRANSCRIBING: 'info',
  SUMMARIZING: 'info',
  RETRY_PENDING: 'warning',
  FAILED: 'error',
  READY: 'success',
  NO_SPEECH: 'neutral',
};

/** The steps the section shows, in order, with where a state stands among them. */
export const STEPS: AiState[] = ['WAITING_FOR_RECORDING', 'QUEUED', 'TRANSCRIBING', 'SUMMARIZING', 'READY'];

export function notAvailableText(reason: AiNotAvailableReason | null): TranslationKey {
  switch (reason) {
    case 'NOT_ELIGIBLE':
      return 'callAi.na.NOT_ELIGIBLE';
    case 'RECORDING_PARTIAL':
    case 'RECORDING_FAILED':
    case 'NO_RECORDING':
      return 'callAi.na.NO_WHOLE_RECORDING';
    case 'RECORDING_EXPIRED':
      return 'callAi.na.RECORDING_EXPIRED';
    default:
      return 'callAi.na.NOT_MADE';
  }
}

/** What an administrator is told a job's failure was — our own codes, never a provider's words. */
export function errorText(code: string | null): TranslationKey {
  switch (code) {
    case 'AUDIO_INVALID':
    case 'AUDIO_TOO_LONG':
    case 'AUDIO_TOO_LARGE':
      return 'callAi.error.AUDIO_INVALID';
    case 'AUDIO_MISSING':
      return 'callAi.error.AUDIO_MISSING';
    case 'AUDIO_TOOLS_MISSING':
      return 'callAi.error.AUDIO_TOOLS_MISSING';
    case 'AUDIO_PROCESSING_FAILED':
    case 'WORK_DISK_LOW':
      return 'callAi.error.AUDIO_PROCESSING_FAILED';
    case 'STORAGE_UNAVAILABLE':
      return 'callAi.error.STORAGE_UNAVAILABLE';
    case 'PROVIDER_RATE_LIMITED':
    case 'PROVIDER_UNAVAILABLE':
    case 'PROVIDER_TIMEOUT':
      return 'callAi.error.PROVIDER_UNAVAILABLE';
    case 'PROVIDER_AUTH':
      return 'callAi.error.PROVIDER_AUTH';
    case 'PROVIDER_QUOTA':
      return 'callAi.error.PROVIDER_QUOTA';
    case 'PROVIDER_REJECTED':
      return 'callAi.error.PROVIDER_REJECTED';
    case 'SUMMARY_REFUSED':
    case 'SUMMARY_INCOMPLETE':
    case 'SUMMARY_INVALID':
      return 'callAi.error.SUMMARY_INVALID';
    case 'RECORDING_EXPIRED':
      return 'callAi.error.RECORDING_EXPIRED';
    case 'JOB_TIMEOUT':
    case 'LEASE_LOST':
      return 'callAi.error.INTERRUPTED';
    default:
      return 'callAi.error.INTERNAL';
  }
}

export const BLOCKER_LABEL: Record<AiBlocker, TranslationKey> = {
  CALLS_AI_DISABLED: 'callAi.blocker.CALLS_AI_DISABLED',
  OPENAI_KEY_MISSING: 'callAi.blocker.OPENAI_KEY_MISSING',
  STORAGE_NOT_SET_UP: 'callAi.blocker.STORAGE_NOT_SET_UP',
  AUDIO_TOOLS_MISSING: 'callAi.blocker.AUDIO_TOOLS_MISSING',
  MODELS_NOT_CONFIGURED: 'callAi.blocker.MODELS_NOT_CONFIGURED',
  SETTING_OFF: 'callAi.blocker.SETTING_OFF',
};

/** `m:ss`, or `h:mm:ss` past an hour — where a part of the transcript sits in the recording. */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}
